import React from 'react';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { determinePostAuthRoute, getUserRoles } from '../utils/authRouting';

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
}

const Auth0ProviderWithNavigate: React.FC<Auth0ProviderWithNavigateProps> = ({ children }) => {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const redirectUri = window.location.origin;

  if (!domain || !clientId || !audience) {
    // Handle missing configuration - maybe render an error message
    // This duplicates the check in main.tsx but ensures safety here
    console.error("Auth0 configuration missing in Auth0ProviderWithNavigate");
    return <div>Auth0 configuration is missing.</div>;
  }

  const onRedirectCallback = async (appState?: AppState, user?: any) => {
    console.log("[Auth0ProviderWithNavigate] onRedirectCallback triggered");
    console.log("[Auth0ProviderWithNavigate] Callback URL:", window.location.href);
    console.log("[Auth0ProviderWithNavigate] AppState:", appState);
    console.log("[Auth0ProviderWithNavigate] User:", user);

    // Set a flag to indicate this is a post-login redirect
    sessionStorage.setItem('auth0_post_login', 'true');

    // Set flag to indicate smart routing is handling the navigation
    // This prevents AuthenticatedUserRedirect from interfering with any smart routing logic
    sessionStorage.setItem('smart_routing_active', 'true');

    // Check for auto-trial intent first
    if (appState?.autoStartTrial && appState?.intent === 'trial_direct') {
      console.log("[Auth0ProviderWithNavigate] Auto-starting trial for new user");
      
      try {
        // Auto-start trial for new authenticated users
        navigate('/trial-setup', { 
          replace: true,
          state: { 
            fromTrialSignup: true,
            autoStartTrial: true
          }
        });
        return;
      } catch (error) {
        console.error("[Auth0ProviderWithNavigate] Auto-trial failed:", error);
        // Clear smart routing flag on error
        sessionStorage.removeItem('smart_routing_active');
        // Fallback to checkout on error
        navigate('/checkout?plan=trial', { replace: true });
        return;
      }
    }

    // Use smart routing to determine destination
    try {
      const userRoles = getUserRoles(user);
      const destination = determinePostAuthRoute(user, appState, userRoles);
      
      console.log("[Auth0ProviderWithNavigate] Smart routing destination:", destination);
      
      navigate(destination, { 
        replace: true,
        state: { 
          fromAuth: true, 
          originalIntent: appState?.intent,
          userRoles: userRoles
        }
      });
    } catch (error) {
      console.error("[Auth0ProviderWithNavigate] Smart routing failed:", error);
      // Fallback to dashboard on error
      navigate('/dashboard', { 
        replace: true, 
        state: { fromLogin: true, fallback: true } 
      });
    }
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: 'openid profile email read:trades write:trades' // Include profile scopes for user info
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithNavigate;
